import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import * as IoIcons from 'react-icons/io5';
import SafeIcon from './common/SafeIcon';
import './App.css';

const { FiX, FiClock, FiMapPin, FiCalendar, FiUsers, FiTrendingUp, FiZap, FiStar, FiCheck, FiChevronDown, FiChevronUp, FiPhone } = FiIcons;
const { FaWhatsapp, FaRocket, FaUsers, FaBrain, FaTrophy, FaGift } = FaIcons;
const { IoLocationSharp, IoTimeSharp, IoCalendarSharp } = IoIcons;

// Payment URL base - update this for production
const PAYMENT_BASE_URL = 'https://paymentz.aifunnels.app/product/21362/page/20487?new_2=true&staffId=65130&ac=1';

// Pabbly webhook URL
const WEBHOOK_URL = 'https://connect.pabbly.com/workflow/sendwebhookdata/IjU3NjYwNTY0MDYzNTA0Mzc1MjY0NTUzYzUxM2Ii_pc';

// Country codes for phone input
const countryCodes = [
  { code: "+91", label: "IN +91", flag: "🇮🇳" },
  { code: "+1", label: "US +1", flag: "🇺🇸" },
  { code: "+44", label: "UK +44", flag: "🇬🇧" },
  { code: "+971", label: "AE +971", flag: "🇦🇪" },
  { code: "+61", label: "AU +61", flag: "🇦🇺" },
  { code: "+65", label: "SG +65", flag: "🇸🇬" }
];

// Utility function to get URL parameters
const getUrlParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    campaign_id: params.get('campaign_id') || '',
    adset_id: params.get('adset_id') || '',
    ad_id: params.get('ad_id') || ''
  };
};

// Utility function to save data to localStorage
const saveFormData = (data) => {
  try {
    localStorage.setItem('landingPageFormData', JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save form data:', error);
  }
};

function App() {
  const [showModal, setShowModal] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+91', // Default to India
    profession: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [readMore, setReadMore] = useState({});
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Get URL parameters on component mount
  const urlParams = useMemo(() => getUrlParams(), []);

  // Event date: 15th November 2025, 9:00 AM IST
  const eventDate = useMemo(() => new Date('2025-11-15T09:00:00+05:30'), []);

  // Previous batch feedback videos
  const previousBatchVideos = [
    { id: 101, embedId: "1nu642NqZeE", title: "Previous Attendee Feedback 1" },
    { id: 102, embedId: "Y2EyyLUUx0I", title: "Previous Attendee Feedback 2" },
    { id: 103, embedId: "mgnZm288gsM", title: "Previous Attendee Feedback 3" },
    { id: 104, embedId: "xMan3E_uRA8", title: "Previous Attendee Feedback 4" },
    { id: 105, embedId: "ffPMqhJsmmw", title: "Previous Attendee Feedback 5" },
    { id: 106, embedId: "EaDiNGQRqlo", title: "Previous Attendee Feedback 6" }
  ];

  // Testimonials data - 6 cards
  const testimonials = [
    {
      id: 1,
      name: "Rajarishi",
      role: "ஞான மையம்",
      image: "https://storage.files-vault.com/uploads/1761583364-9vtQoBGO4b.png",
      quote: "என்னுடைய முதல் ஒரு இலட்சத்தை இங்கு சம்பாதித்தேன் ஆங்கிலம் அறியாமலேயே...! The WhatsApp automation strategies completely transformed my spiritual coaching business."
    },
    {
      id: 2,
      name: "Ahamed Junaith",
      role: "College Professor",
      image: "https://storage.files-vault.com/uploads/1760941910-ht57JBdJ4f.webp",
      quote: "I've earned my First 35 Lakhs Online while working as a full-time employee. The automation tools freed up my time to focus on what matters most."
    },
    {
      id: 3,
      name: "Fathima Subiya",
      role: "Bridal Makeup",
      image: "https://storage.files-vault.com/uploads/1760942062-cWbhjZc7n9.webp",
      quote: "I repaid my loan in the first month itself... WhatsApp funnels helped me book clients 24/7 without constant manual follow-ups."
    },
    {
      id: 4,
      name: "Valarmathi Srinivasan",
      role: "Breath Work",
      image: "https://storage.files-vault.com/uploads/1760942066-tiqyCho8Gf.webp",
      quote: "I crossed my First One Crore Income by Automating my Coaching Business. The AI chatbots handle inquiries while I focus on delivering value."
    },
    {
      id: 5,
      name: "Arjumand",
      role: "Quranic Coach",
      image: "https://storage.files-vault.com/uploads/1760942064-I6HBtoRq5t.webp",
      quote: "Got my Hall of Fame award after earning my first ₹300,000... The automated follow-up sequences converted leads I would have lost manually."
    },
    {
      id: 6,
      name: "Maria Sundar",
      role: " Stock Trader Coach",
      image: "https://storage.files-vault.com/uploads/1760942061-nsFEgCbsBd.webp",
      quote: "1,000+ Paid Members & 3.8 Lakhs Collected in Just 60 Days with Trading Webinars"
    }
  ];

  // Video testimonials data
  const videoTestimonials = [
    { id: 1, embedId: "v3EEtDIaC3w", title: "Success Story - Digital Marketing Agency" },
    { id: 2, embedId: "IrfPwkcSTB0", title: "E-commerce Transformation" },
    { id: 3, embedId: "F26JiMS-w4I", title: "Coaching Business Automation" }
  ];

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = eventDate.getTime() - now;

      if (distance > 0) {
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [eventDate]);

  // Auto-popup after 8 seconds
  useEffect(() => {
    const hasShown = sessionStorage.getItem('modalFormShown');
    if (hasShown === 'true') return;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    const timer = setTimeout(() => {
      setShowModal(true);
    }, 8000);

    return () => clearTimeout(timer);
  }, []);

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    sessionStorage.setItem('modalFormShown', 'true');
  }, []);

  // Handle input changes
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [formErrors]);

  // Form validation
  const validateForm = useCallback(() => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Full name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Email is invalid';
    if (!formData.phone.trim()) errors.phone = 'Phone number is required';
    else if (!/^\d{7,15}$/.test(formData.phone.replace(/\s/g, ''))) errors.phone = 'Please enter a valid phone number';
    if (!formData.profession.trim()) errors.profession = 'Profession is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  // Send data to webhook
  const sendToWebhook = async (data) => {
    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`Webhook failed: ${response.status}`);
      }

      return true;
    } catch (error) {
      console.error('Webhook submission failed:', error);
      return false;
    }
  };

  // Handle form submission
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    // Track form submission
    if (typeof fbq !== 'undefined') {
      fbq('track', 'Lead');
    }

    // Prepare data for webhook
    const phoneWithCountry = `${formData.countryCode}${formData.phone.replace(/^\+/, '')}`;
    const webhookData = {
      name: formData.name,
      email: formData.email,
      phone: phoneWithCountry,
      profession: formData.profession,
      ...urlParams, // Include campaign parameters
      timestamp: new Date().toISOString(),
      source: 'landing_page'
    };

    // Send to webhook
    const webhookSuccess = await sendToWebhook(webhookData);

    if (webhookSuccess) {
      // Save form data to localStorage for payment page prefill
      const savedData = {
        name: formData.name,
        email: formData.email,
        phone: phoneWithCountry,
        timestamp: new Date().toISOString()
      };
      saveFormData(savedData);

      // Build payment URL with encoded parameters
      const params = `&first%20name=${encodeURIComponent(formData.name)}&email=${encodeURIComponent(formData.email)}&phone%20no=${encodeURIComponent(phoneWithCountry)}`;
      const paymentUrl = PAYMENT_BASE_URL + params;

      // Open payment page in new tab
      window.open(paymentUrl, '_blank', 'noopener,noreferrer');

      // Show success message
      alert('Thank you for registering! Redirecting to payment page...');
      handleCloseModal();
    } else {
      alert('There was an error submitting your registration. Please try again.');
    }

    setIsSubmitting(false);
  }, [formData, validateForm, handleCloseModal, urlParams]);

  // Open registration modal
  const openModal = useCallback(() => setShowModal(true), []);

  // Toggle read more for testimonials
  const toggleReadMore = useCallback((id) => {
    setReadMore(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  // FAQ data
  const faqs = [
    {
      question: "இந்த summit-ல் நான் என்ன கற்றுக்கொள்வேன்?",
      answer: "நீங்கள் WhatsApp AI automation-ஐ master செய்வீர்கள், high-converting funnels உருவாக்கக் கற்றுக்கொள்வீர்கள், advanced chatbot உத்திகளைக் கண்டறிவீர்கள், மேலும் business growth-க்கான சமீபத்திய AI tools-களுடன் hands-on அனுபவம் பெறுவீர்கள்."
    },
    {
      question: "இது beginners-களுக்கு ஏற்றதா?",
      answer: "நிச்சயமாக! இந்த workshop complete beginners முதல் தங்கள் WhatsApp automation skills-களை upgrade செய்ய விரும்பும் experienced marketers வரை அனைத்து levels-களுக்கும் வடிவமைக்கப்பட்டுள்ளது."
    },
    {
      question: "டிக்கெட் விலையில் என்னென்ன அடங்கும்?",
      answer: "உங்கள் டிக்கெட்டில் full-day workshop, மதிய உணவு, 2 high tea sessions (snacks-உடன்), networking வாய்ப்புகள், exclusive resources, மற்றும் எங்கள் community-க்கான lifetime access ஆகியவை அடங்கும்."
    },
    {
      question: "நான் வர முடியாவிட்டால் refund கிடைக்குமா?",
      answer: "Event-க்கு 7 நாட்களுக்கு முன்பு வரை full refund வழங்கப்படும். அதன்பிறகு, உங்கள் டிக்கெட்டை வேறு ஒருவருக்கு transfer செய்யலாம் அல்லது எங்கள் online materials-களை access செய்யலாம்."
    },
    {
      question: "Networking வாய்ப்புகள் இருக்குமா?",
      answer: "ஆம்! உங்களைப் போன்றே சிந்திக்கும் professionals-உடன் connect செய்ய, breaks-இன் போது dedicated networking sessions, group activities, மற்றும் informal meetups ஏற்பாடு செய்யப்பட்டுள்ளன."
    }
  ];

  return (
    <div className="app">
      {/* Floating particles background */}
      <div className="particles">
        {[...Array(20)].map((_, i) => (
          <div key={i} className={`particle particle-${i % 3}`}></div>
        ))}
      </div>

      {/* Hero Section */}
      <section className="hero">
        {/* Premium Red Banner */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="premium-banner"
        >
          <div className="banner-content">
            <span className="banner-text">10xAI Founder ஒரு நாள் பயிற்சி தமிழில்</span>
          </div>
        </motion.div>

        <div className="hero-content">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hero-text"
          >
            <h1 className="hero-title">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.8 }}
                className="hero-headline-wrapper"
              >
                <span className="hero-headline">
                  🚀 Tamil Business Owners-க்கு Exclusive 1-Day
                </span>
              </motion.div>
              <motion.span
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9, duration: 0.8 }}
                className="highlight"
              >
                WhatsApp AI Funnels Summit
              </motion.span>
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2, duration: 0.8 }}
              >
                – சென்னை 🇮🇳
              </motion.span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              className="hero-subtitle"
            >
              🎯 இந்த ஆண்டின் மிகவும் எதிர்பார்க்கப்பட்ட In-Person Workshop!<br />
              உங்கள் business-ஐ automate செய்யவும், conversions-ஐ அதிகரிக்கவும், WhatsApp AI Funnels-ஐ master செய்யவும் கற்றுக்கொள்ளுங்கள் — அனைத்தும் ஒரே நாளில்!
            </motion.p>

            {/* Event Details */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.8, duration: 0.8 }}
              className="event-details"
            >
              <div className="detail-item">
                <SafeIcon icon={IoCalendarSharp} />
                <span>📅 <strong>தேதி:</strong> 15th November 2025</span>
              </div>
              <div className="detail-item">
                <SafeIcon icon={IoTimeSharp} />
                <span>🕘 <strong>நேரம்:</strong> 9:00 AM – 6:00 PM</span>
              </div>
              <div className="detail-item">
                <SafeIcon icon={IoLocationSharp} />
                <span>📍 <strong>இடம்:</strong> Hotel Chennai Le Palace - Porur</span>
              </div>
            </motion.div>

            {/* Enhanced Pricing Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 2.1, duration: 0.8 }}
              className="pricing"
            >
              <div className="enhanced-price-card">
                <div className="price-header">
                  <h3 className="price-title">🚀 Exclusive 1-Day WhatsApp AI Funnels Summit – Chennai</h3>
                </div>

                <div className="price-section">
                  <div className="original-price">
                    <span className="original-price-label">Original Price:</span>
                    <span className="original-price-value">₹2999.00</span>
                  </div>

                  <div className="price-breakdown">
                    <div className="breakdown-item">
                      <span>₹1693.22</span>
                    </div>
                    <div className="breakdown-item">
                      <span>GST ₹305.78</span>
                    </div>
                  </div>

                  <div className="final-price">
                    <span className="final-price-label">Amount to be paid:</span>
                    <span className="final-price-value">₹1999.00</span>
                  </div>
                </div>

                <div className="included-section">
                  <h4 className="included-title">✅ Included</h4>
                  <div className="benefits-list">
                    <div className="benefit-item">
                      <SafeIcon icon={FiCheck} className="benefit-icon" />
                      <span>Game-Changing Workbook</span>
                    </div>
                    <div className="benefit-item">
                      <SafeIcon icon={FiCheck} className="benefit-icon" />
                      <span>Stationery Kit</span>
                    </div>
                    <div className="benefit-item">
                      <SafeIcon icon={FiCheck} className="benefit-icon" />
                      <span>2× High Tea & Snacks</span>
                    </div>
                    <div className="benefit-item">
                      <SafeIcon icon={FiCheck} className="benefit-icon" />
                      <span>Veg/Non-Veg Buffet Lunch</span>
                    </div>
                    <div className="benefit-item">
                      <SafeIcon icon={FiCheck} className="benefit-icon" />
                      <span>Networking with Like-Minded Tamil Entrepreneurs</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Countdown Timer */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2.4, duration: 0.8 }}
              className="countdown"
            >
              <h3>⏳ Event தொடங்குகிறது:</h3>
              <div className="countdown-timer">
                <div className="time-unit">
                  <span className="time-value">{timeLeft.days}</span>
                  <span className="time-label">Days</span>
                </div>
                <div className="time-unit">
                  <span className="time-value">{timeLeft.hours}</span>
                  <span className="time-label">Hours</span>
                </div>
                <div className="time-unit">
                  <span className="time-value">{timeLeft.minutes}</span>
                  <span className="time-label">Minutes</span>
                </div>
                <div className="time-unit">
                  <span className="time-value">{timeLeft.seconds}</span>
                  <span className="time-label">Seconds</span>
                </div>
              </div>
            </motion.div>

            {/* CTA Button */}
            <motion.button
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2.7, duration: 0.8 }}
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.95 }}
              className="cta-button primary"
              onClick={openModal}
            >
              எனது Seat-ஐ இப்போதே Reserve செய்யுங்கள் – ₹1999 🚀
            </motion.button>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 3.0, duration: 0.8 }}
              className="urgency-text"
            >
              ⏳ Seat-கள் வேகமாக நிரம்புகின்றன — இப்போதே book செய்யுங்கள்!
            </motion.p>
          </motion.div>
        </div>

        {/* Floating WhatsApp Icons */}
        <div className="floating-icons">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className={`floating-icon icon-${i}`}
              animate={{
                y: [0, -20, 0],
                rotate: [0, 10, -10, 0],
              }}
              transition={{
                duration: 3 + i * 0.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <SafeIcon icon={FaWhatsapp} />
            </motion.div>
          ))}
        </div>
      </section>

      {/* Previous Batch Feedback Section */}
      <section className="previous-batch-videos">
        <div className="container">
          <motion.h2
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="section-title"
          >
            💬 எங்கள் Previous Batch Attendees-இன் Feedback
          </motion.h2>

          <div className="video-grid">
            {previousBatchVideos.map((video, index) => (
              <motion.button
                key={video.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="video-thumb"
                aria-label={`Play ${video.title}`}
                onClick={() => setSelectedVideo(video)}
              >
                <img
                  src={`https://img.youtube.com/vi/${video.embedId}/hqdefault.jpg`}
                  alt={video.title}
                  loading="lazy"
                />
                <span className="play-overlay">▶</span>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Why Attend Section */}
      <section id="why-attend" className="why-attend">
        <div className="container">
          <motion.h2
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="section-title"
          >
            🎯 இந்த Summit-ல் ஏன் கலந்துகொள்ள வேண்டும்?
          </motion.h2>

          <div className="benefits-grid">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="benefit-card"
            >
              <div className="benefit-icon">
                <SafeIcon icon={FaBrain} />
              </div>
              <h3>🧠 கற்றுக்கொள்ளுங்கள் (Learn)</h3>
              <p>உங்கள் business operations மற்றும் customer engagement-ஐ மாற்றியமைக்கும் cutting-edge WhatsApp AI automation நுட்பங்களை Master செய்யுங்கள்.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="benefit-card"
            >
              <div className="benefit-icon">
                <SafeIcon icon={FaUsers} />
              </div>
              <h3>🤝 நெட்வொர்க் (Network)</h3>
              <p>Industry experts, automation pros, மற்றும் AI மூலம் தங்கள் business-ஐ scale செய்யும் தொழில்முனைவோருடன் Connect செய்யுங்கள்.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="benefit-card"
            >
              <div className="benefit-icon">
                <SafeIcon icon={FaRocket} />
              </div>
              <h3>🚀 ஆட்டோமேட் (Automate)</h3>
              <p>உங்களுக்கு தினமும் பல மணிநேரத்தை மிச்சப்படுத்தும், நிரூபிக்கப்பட்ட automation உத்திகளை Implement செய்யுங்கள்.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Speaker Section */}
      <section id="speaker" className="speaker">
        <div className="container">
          <motion.h2
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="section-title"
          >
            🎤 உங்கள் Expert Speaker-ஐ சந்தியுங்கள்
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="speaker-card"
          >
            <div className="speaker-image">
              <img
                src="https://storage.files-vault.com/uploads/1760939277-omcsSPU38t.png"
                alt="Expert Speaker"
                loading="lazy"
              />
            </div>
            <div className="speaker-info">
              <h3>Abdul Hakkim</h3>
              <p className="speaker-title">WhatsApp AI Automation Expert & Serial Entrepreneur</p>
              <p className="speaker-bio">
                With over 8 years of experience in marketing automation and AI, Abdul Hakkim has helped 50+ businesses scale their operations using WhatsApp AI funnels. His strategies have generated over ₹10 crores in revenue for his clients across various industries.
              </p>
              <div className="speaker-achievements">
                <span>✅ 50+ Businesses Transformed</span>
                <span>✅ ₹10+ Crores Revenue Generated</span>
                <span>✅ 8+ Years Experience</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Event Agenda */}
      <section id="agenda" className="agenda">
        <div className="container">
          <motion.h2
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="section-title"
          >
            📅 Event Agenda
          </motion.h2>

          <div className="agenda-timeline">
            {[
              { time: "9:00 AM - 9:30 AM", title: "Registration & Welcome Coffee", desc: "மற்ற பங்கேற்பாளர்களுடன் Network செய்யுங்கள்" },
              { time: "9:30 AM - 11:00 AM", title: "WhatsApp Business API Mastery", desc: "அடிப்படைகள் மற்றும் advanced features-களைக் கற்றுக்கொள்ளுங்கள்" },
              { time: "11:00 AM - 11:15 AM", title: "High Tea Break", desc: "Refreshments and networking" },
              { time: "11:15 AM - 12:30 PM", title: "AI Chatbot Development", desc: "Build intelligent conversational flows" },
              { time: "12:30 PM - 1:30 PM", title: "Lunch & Networking", desc: "Delicious meal and meaningful connections" },
              { time: "1:30 PM - 3:00 PM", title: "Advanced Funnel Strategies", desc: "High-converting automation sequences" },
              { time: "3:00 PM - 3:15 PM", title: "Snack Break", desc: "Quick refreshments" },
              { time: "3:15 PM - 4:30 PM", title: "Live Implementation Workshop", desc: "Hands-on practice with real examples" },
              { time: "4:30 PM - 5:30 PM", title: "Q&A & Troubleshooting", desc: "Get your specific questions answered" },
              { time: "5:30 PM - 6:00 PM", title: "Closing & Next Steps", desc: "Resources and community access" }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="agenda-item"
              >
                <div className="agenda-time">{item.time}</div>
                <div className="agenda-content">
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bonuses Section */}
      <section id="bonuses" className="bonuses">
        <div className="container">
          <motion.h2
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="section-title"
          >
            🎁 Exclusive Bonuses & Offers
          </motion.h2>

          <div className="bonuses-grid">
            {[
              { icon: FaGift, title: "Premium Templates Pack", value: "Worth ₹5,999", desc: "பல்வேறு industries-களுக்கான ready-to-use WhatsApp funnel templates" },
              { icon: FaTrophy, title: "Lifetime Community Access", value: "Worth ₹2,999", desc: "எங்கள் exclusive WhatsApp automation community-ல் சேருங்கள்" },
              { icon: FaRocket, title: "1-on-1 Strategy Session", value: "Worth ₹9,999", desc: "உங்கள் business automation-ஐ optimize செய்ய personal consultation" },
              { icon: FaBrain, title: "AI Tools Masterclass", value: "Worth ₹3,999", desc: "Business growth-க்கான சமீபத்திய AI tools பற்றிய bonus session" }
            ].map((bonus, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="bonus-card"
              >
                <div className="bonus-icon">
                  <SafeIcon icon={bonus.icon} />
                </div>
                <h3>{bonus.title}</h3>
                <div className="bonus-value">{bonus.value}</div>
                <p>{bonus.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="total-value"
          >
            <h3>Total Bonus Value: ₹22,996</h3>
            <p>🎯 அனைத்தும் உங்கள் summit ticket-உடன் FREE!</p>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section - 6 Cards */}
      <section className="testimonials">
        <div className="container">
          <motion.h2
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="section-title"
          >
            💬 எங்கள் Attendees என்ன சொல்கிறார்கள்
          </motion.h2>

          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <motion.article
                key={testimonial.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="testimonial-card"
                role="article"
                aria-labelledby={`testi-name-${testimonial.id}`}
              >
                <div className="testi-image">
                  <img
                    src={testimonial.image}
                    alt={`${testimonial.name} - ${testimonial.role}`}
                    loading="lazy"
                  />
                </div>
                <div className="testi-body">
                  <h3 id={`testi-name-${testimonial.id}`}>{testimonial.name}</h3>
                  <p className="testi-role">{testimonial.role}</p>
                  <p className="testi-quote">
                    {readMore[testimonial.id] || testimonial.quote.length <= 160
                      ? testimonial.quote
                      : `${testimonial.quote.slice(0, 160)}...`}
                  </p>
                  {testimonial.quote.length > 160 && (
                    <button
                      className="read-more"
                      onClick={() => toggleReadMore(testimonial.id)}
                      aria-expanded={!!readMore[testimonial.id]}
                    >
                      {readMore[testimonial.id] ? 'Read less' : 'Read more'}
                    </button>
                  )}
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Video Testimonials Section */}
      <section className="video-testimonials">
        <div className="container">
          <motion.h2
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="section-title"
          >
            🎥 Success Stories in Action
          </motion.h2>

          <div className="video-grid">
            {videoTestimonials.map((video, index) => (
              <motion.button
                key={video.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="video-thumb"
                aria-label={`Play ${video.title}`}
                onClick={() => setSelectedVideo(video)}
              >
                <img
                  src={`https://img.youtube.com/vi/${video.embedId}/hqdefault.jpg`}
                  alt={video.title}
                  loading="lazy"
                />
                <span className="play-overlay">▶</span>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Video Modal */}
        <AnimatePresence>
          {selectedVideo && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="video-modal-overlay"
              onClick={() => setSelectedVideo(null)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="video-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setSelectedVideo(null)}
                  className="close-button"
                  aria-label="Close video"
                >
                  <SafeIcon icon={FiX} />
                </button>
                <iframe
                  src={`https://www.youtube.com/embed/${selectedVideo.embedId}?autoplay=1&rel=0`}
                  title={selectedVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      {/* FAQ Section */}
      <section className="faq">
        <div className="container">
          <motion.h2
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="section-title"
          >
            ❓ அடிக்கடி கேட்கப்படும் கேள்விகள் (FAQ)
          </motion.h2>

          <div className="faq-list">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="faq-item"
              >
                <button
                  className="faq-question"
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  aria-expanded={expandedFaq === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <span>{faq.question}</span>
                  <SafeIcon icon={expandedFaq === index ? FiChevronUp : FiChevronDown} />
                </button>
                <AnimatePresence>
                  {expandedFaq === index && (
                    <motion.div
                      id={`faq-answer-${index}`}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="faq-answer"
                    >
                      <p>{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="cta-content"
          >
            <h2>🚀 இந்த Game-Changing வாய்ப்பைத் தவறவிடாதீர்கள்!</h2>
            <p>ஏற்கனவே WhatsApp AI automation மூலம் தங்கள் business-களை மாற்றியமைக்கும் நூற்றுக்கணக்கான தொழில்முனைவோருடன் சேருங்கள்.</p>

            <div className="cta-features">
              <div className="cta-feature">
                <SafeIcon icon={FiCheck} />
                <span>🥗 மதிய உணவு + 2 High Tea & Snacks உட்பட</span>
              </div>
              <div className="cta-feature">
                <SafeIcon icon={FiCheck} />
                <span>💼 Industry experts மற்றும் automation pros-களுடன் Network செய்யுங்கள்</span>
              </div>
              <div className="cta-feature">
                <SafeIcon icon={FiCheck} />
                <span>🎁 ₹22,996 மதிப்புள்ள bonuses FREE-ஆக சேர்க்கப்பட்டுள்ளது</span>
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.95 }}
              className="cta-button primary large"
              onClick={openModal}
            >
              WhatsApp AI Revolution-ல் சேருங்கள் 🔥
            </motion.button>

            <p className="cta-urgency">⚡ Early bird விலையில் சில Seat-கள் மட்டுமே மீதம் உள்ளன!</p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3>தொடர்புக்கு (Support)</h3>
              <p>📧 <a href="mailto:support@yinguru.com">support@yinguru.com</a></p>
              <p>📱 <a href="tel:+919884899080">+91 9884899080</a></p>
            </div>
            <div className="footer-section">
              <h3>Quick Links</h3>
              <a href="#agenda">Event Agenda</a>
              <a href="#speaker">Speaker</a>
              <a href="#bonuses">Bonuses</a>
              <a href="https://funnelsdone.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              <a href="https://funnelsdone.com/refund-policy" target="_blank" rel="noopener noreferrer">Refund Policy</a>
            </div>
            <div className="footer-section">
              <h3>Follow Us</h3>
              <div className="social-links">
                <a href="https://wa.me/919884899080" target="_blank" rel="noopener noreferrer">
                  <SafeIcon icon={FaWhatsapp} />
                </a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 AI Funnels. All rights reserved.</p>
            <p className="disclaimer">
              This site is not part of the Facebook website or Facebook Inc. Additionally, this site is NOT endorsed by Facebook in any way. FACEBOOK is a trademark of FACEBOOK, Inc.
            </p>
          </div>
        </div>
      </footer>

      {/* Registration Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-overlay"
            onClick={handleCloseModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              transition={{ duration: 0.36, ease: 'easeOut' }}
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="modal-close" onClick={handleCloseModal}>
                <SafeIcon icon={FiX} />
              </button>

              <div className="modal-header">
                <h2>🚀 உங்கள் Seat-ஐ Reserve செய்யுங்கள்</h2>
                <p>மிகவும் exclusive-ஆன WhatsApp AI Summit-ல் உங்கள் இடத்தைப் பாதுகாத்திடுங்கள்!</p>
              </div>

              <form onSubmit={handleSubmit} className="registration-form">
                <div className="form-group">
                  <input
                    type="text"
                    name="name"
                    placeholder="முழுப் பெயர் (Full Name) *"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={formErrors.name ? 'error' : ''}
                  />
                  {formErrors.name && <span className="error-message" role="alert">{formErrors.name}</span>}
                </div>

                <div className="form-group">
                  <input
                    type="email"
                    name="email"
                    placeholder="மின்னஞ்சல் (Email Address) *"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={formErrors.email ? 'error' : ''}
                  />
                  {formErrors.email && <span className="error-message" role="alert">{formErrors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="phone">
                    <SafeIcon icon={FiPhone} className="input-icon" />
                    தொலைபேசி எண் (Phone Number) *
                  </label>
                  <div className="phone-input-group" role="group" aria-labelledby="phone">
                    <select
                      id="countryCode"
                      name="countryCode"
                      className="country-select"
                      value={formData.countryCode}
                      onChange={(e) => handleInputChange('countryCode', e.target.value)}
                      aria-label="Country code"
                    >
                      {countryCodes.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.flag} {c.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      id="phone"
                      inputMode="tel"
                      name="phone"
                      className={`phone-input ${formErrors.phone ? 'error' : ''}`}
                      placeholder="9884899080"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value.replace(/\D/g, ''))}
                      aria-describedby={formErrors.phone ? 'phone-error' : undefined}
                      maxLength="15"
                    />
                  </div>
                  {formErrors.phone && (
                    <span id="phone-error" className="error-message" role="alert">
                      {formErrors.phone}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <select
                    name="profession"
                    value={formData.profession}
                    onChange={(e) => handleInputChange('profession', e.target.value)}
                    className={formErrors.profession ? 'error' : ''}
                  >
                    <option value="">உங்கள் தொழிலைத் தேர்ந்தெடுக்கவும் (Profession) *</option>
                    <option value="business-owner">Business Owner</option>
                    <option value="entrepreneur">Entrepreneur</option>
                    <option value="marketer">Digital Marketer</option>
                    <option value="consultant">Consultant</option>
                    <option value="freelancer">Freelancer</option>
                    <option value="student">Student</option>
                    <option value="other">Other</option>
                  </select>
                  {formErrors.profession && <span className="error-message" role="alert">{formErrors.profession}</span>}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="submit-button"
                >
                  {isSubmitting ? 'Processing...' : 'எனது Early Bird Access-ஐ Unlock செய்யுங்கள் 💥'}
                </button>
              </form>

              <div className="modal-footer">
                <p>🔒 உங்கள் தகவல்கள் பாதுகாப்பாக வைக்கப்படும்.</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating WhatsApp Button */}
      <motion.a
        href="https://wa.me/919884899080"
        target="_blank"
        rel="noopener noreferrer"
        className="floating-whatsapp"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        animate={{
          y: [0, -10, 0],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <SafeIcon icon={FaWhatsapp} />
      </motion.a>
    </div>
  );
}

export default App;

/* Integration Summary:
✓ Event date updated to 15th November 2025
✓ Price updated to ₹1999 (from ₹1499)
✓ Price breakdown updated accordingly:
  - ₹1693.22 (base amount)
  - GST ₹305.78
  - Total: ₹1999.00
✓ CTA button text updated to reflect new price
✓ Event details section updated with new date
✓ Countdown timer will now count down to November 15th, 2025
✓ All other functionality remains intact
*/